// +----------------------------------------------------------------------
// | AI 对话页（Phase 9）
// |
// | 布局：左侧会话列表 + 右侧消息流。
// | 回复走流式，边生成边显示；流式不可用时自动降级为非流式一次性返回。
// |
// | 一个刻意的取舍：/chat 不带 id 时是「新对话」状态，第一条消息发出去
// | 才真正建会话 —— 避免用户点了「新对话」又反悔，列表里留一堆空壳。
// +----------------------------------------------------------------------
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AIMessage from '../../components/AIMessage';
import { toast } from '../../components/Toast';
import {
  createSession,
  deleteSession,
  getSession,
  listSessions,
  renameSession,
  sendMessage,
  streamMessage,
} from '../../api/chat';
import { fromNow } from '../../lib/format';
import './Chat.less';

// 不知道聊什么？点一下填进输入框
const STARTERS = [
  '我今天有点烦，想找人说说',
  '我最近总在纠结要不要换工作',
  '怎么才能不那么在意别人的看法',
  '帮我理理：周末该出门还是在家',
];

/**
 * 极轻量的行内渲染：只认 **加粗** 和 `代码`，不做完整 Markdown
 *
 * 用 React 元素拼而不是 dangerouslySetInnerHTML —— 模型输出属于外部输入，
 * 不给自己留 XSS 的口子。
 */
function Inline({ text }) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*|`[^`]+`)/g);

  return (
    <>
      {parts.map((part, index) => {
        if (/^\*\*[^*]+\*\*$/.test(part)) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        if (/^`[^`]+`$/.test(part)) {
          return <code key={index}>{part.slice(1, -1)}</code>;
        }
        return part;
      })}
    </>
  );
}

/** 把一段纯文本按行渲染，行首的 1. / - / · 变成列表样式 */
function RichText({ text }) {
  const lines = String(text || '').split('\n');

  return (
    <div className="chat-text">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (trimmed === '') {
          return <div key={index} className="chat-text-gap" />;
        }

        const bullet = /^(\d+[.、)]|[-*·])\s*/.exec(trimmed);
        if (bullet) {
          return (
            <div key={index} className="chat-text-li">
              <span className="chat-text-mark">{/^\d/.test(bullet[0]) ? bullet[0].trim() : '·'}</span>
              <span>
                <Inline text={trimmed.slice(bullet[0].length)} />
              </span>
            </div>
          );
        }

        return (
          <div key={index} className="chat-text-p">
            <Inline text={trimmed} />
          </div>
        );
      })}
    </div>
  );
}

/** 一条 AI 回复底部的小字：这次参考了什么 */
function MessageMeta({ meta }) {
  if (!meta) return null;

  const memoryUsed = Number(meta.memory_used || 0);
  const sources = Array.isArray(meta.external_sources) ? meta.external_sources : [];

  if (memoryUsed <= 0 && sources.length === 0) return null;

  return (
    <div className="chat-meta muted">
      {memoryUsed > 0 && (
        <span>
          参考了 {memoryUsed} 条
          <Link to="/memories">记忆</Link>
        </span>
      )}
      {sources.length > 0 && (
        <span>实时信息 · {sources.map((item) => item.label || item.name).join('、')}</span>
      )}
    </div>
  );
}

export default function Chat() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const currentId = routeId ? Number(routeId) : null;

  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [booting, setBooting] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [error, setError] = useState('');
  const [retryText, setRetryText] = useState('');
  const [sideOpen, setSideOpen] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [editing, setEditing] = useState({ id: 0, title: '' });

  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const bootedRef = useRef(false);

  const current = sessions.find((item) => Number(item.id) === currentId) || null;

  // ------------------------------ 首次进入：拉会话列表 ------------------------------
  useEffect(() => {
    let alive = true;

    listSessions({ page_size: 50 })
      .then((data) => {
        if (!alive) return;

        const list = data.list || [];
        setSessions(list);
        setEnabled(data.enabled !== false);

        // 直接进 /chat：自动接着最近一次聊，避免每次都从空白开始
        if (!routeId && list.length > 0) {
          navigate(`/chat/${list[0].id}`, { replace: true });
        }
      })
      .catch((e) => {
        if (alive) setError(e.message || '会话列表加载失败');
      })
      .finally(() => {
        if (alive) {
          setBooting(false);
          bootedRef.current = true;
        }
      });

    return () => {
      alive = false;
    };
    // 只在首次挂载跑一次：之后切会话不该重拉列表
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------ 切换会话：拉消息 ------------------------------
  useEffect(() => {
    if (!currentId) {
      setMessages([]);
      return undefined;
    }

    let alive = true;
    setLoadingMsgs(true);
    setError('');

    getSession(currentId, { limit: 60 })
      .then((data) => {
        if (!alive) return;
        setMessages(data.messages || []);
        setEnabled(data.enabled !== false);
        if (data.session) {
          setSessions((prev) => {
            const exists = prev.some((item) => Number(item.id) === Number(data.session.id));
            return exists
              ? prev.map((item) => (Number(item.id) === Number(data.session.id) ? { ...item, ...data.session } : item))
              : [data.session, ...prev];
          });
        }
      })
      .catch((e) => {
        if (!alive) return;
        setError(e.message || '这个会话打不开了');
        setMessages([]);
      })
      .finally(() => {
        if (alive) setLoadingMsgs(false);
      });

    return () => {
      alive = false;
    };
  }, [currentId]);

  // ------------------------------ 自动滚到底 ------------------------------
  useEffect(() => {
    const el = bodyRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, streamText, error]);

  // 输入框自适应高度：默认一行，内容多了自动长高（上限交给 CSS max-height）
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [input]);

  // 离开页面时中断正在进行的流
  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
    },
    []
  );

  const upsertSession = useCallback((session) => {
    if (!session) return;

    setSessions((prev) => {
      const exists = prev.some((item) => Number(item.id) === Number(session.id));
      const next = exists
        ? prev.map((item) => (Number(item.id) === Number(session.id) ? { ...item, ...session } : item))
        : [session, ...prev];

      // 最近聊过的排最前
      return next.sort((a, b) => String(b.last_message_at || '').localeCompare(String(a.last_message_at || '')));
    });
  }, []);

  // ------------------------------ 发送 ------------------------------
  const submit = useCallback(
    async (text) => {
      const content = String(text || '').trim();
      if (!content || streaming) return;

      let sessionId = currentId;

      // 新对话状态：先把会话建出来
      if (!sessionId) {
        try {
          const created = await createSession();
          sessionId = created.id;
          upsertSession(created);
          navigate(`/chat/${sessionId}`, { replace: true });
        } catch (e) {
          setError(e.message || '开不了新对话');
          return;
        }
      }

      const localId = `local-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { id: localId, role: 'user', content, meta: {}, created_at: new Date().toISOString() },
      ]);
      setInput('');
      setError('');
      setRetryText('');
      setStreaming(true);
      setStreamText('');

      let acc = '';
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await streamMessage(
          sessionId,
          content,
          {
            onStart: (event) => {
              if (event.user_message) {
                const saved = event.user_message;
                setMessages((prev) => prev.map((item) => (item.id === localId ? saved : item)));
              }
              upsertSession(event.session);
            },
            onDelta: (delta) => {
              acc += delta;
              setStreamText(acc);
            },
            onDone: (event) => {
              if (event.message) {
                setMessages((prev) => [...prev, event.message]);
              }
              upsertSession(event.session);
              setStreamText('');
            },
            onError: (err) => {
              throw err;
            },
          },
          controller.signal
        );
      } catch (streamError) {
        // ---------- 流式失败：先把已吐出来的半截留下，再试非流式 ----------
        const partial = acc.trim();

        if (partial === '') {
          try {
            const fallback = await sendMessage(sessionId, content);

            if (fallback.user_message) {
              const saved = fallback.user_message;
              setMessages((prev) => prev.map((item) => (item.id === localId ? saved : item)));
            }
            if (fallback.message) {
              setMessages((prev) => [...prev, fallback.message]);
            }
            upsertSession(fallback.session);
            setStreamText('');
            return;
          } catch (fallbackError) {
            setError(fallbackError.message || '这次没答上来，稍后再试');
            setRetryText(content);
            setStreamText('');
            return;
          }
        }

        // 有半截内容：重新拉一次会话，把已落库的部分取回来，避免「看到过又消失」
        setStreamText('');
        setError(streamError.message || '这次没答上来，稍后再试');
        setRetryText(content);

        try {
          const fresh = await getSession(sessionId, { limit: 60 });
          setMessages(fresh.messages || []);
        } catch (e) {
          // 拉不回就算了，错误提示已经给出
        }
      } finally {
        abortRef.current = null;
        setStreaming(false);
      }
    },
    [currentId, navigate, streaming, upsertSession]
  );

  const handleKeyDown = (event) => {
    // 中文输入法组合中不触发发送
    if (event.nativeEvent && event.nativeEvent.isComposing) return;

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit(input);
    }
  };

  const handleNew = () => {
    setSideOpen(false);
    setError('');
    navigate('/chat');
  };

  const handleRemove = async (event, session) => {
    event.stopPropagation();

    if (!window.confirm(`删掉「${session.title}」？这段聊天记录就不再显示了。`)) return;

    try {
      await deleteSession(session.id);
      setSessions((prev) => prev.filter((item) => Number(item.id) !== Number(session.id)));
      if (Number(session.id) === currentId) {
        navigate('/chat', { replace: true });
      }
      toast('删掉了');
    } catch (e) {
      toast(e.message || '删除失败');
    }
  };

  const startRename = (event, session) => {
    event.stopPropagation();
    setEditing({ id: session.id, title: session.title });
  };

  const commitRename = async () => {
    const { id, title } = editing;
    setEditing({ id: 0, title: '' });

    const next = String(title || '').trim();
    if (!id || next === '') return;

    try {
      const updated = await renameSession(id, next);
      upsertSession(updated);
    } catch (e) {
      toast(e.message || '改名字失败');
    }
  };

  const canSend = input.trim() !== '' && !streaming;

  return (
    <div className="chat">
      {/* ---------------- 左侧：会话列表 ---------------- */}
      <aside className={`chat-side ${sideOpen ? 'is-open' : ''}`}>
        <div className="chat-side-head">
          <button type="button" className="btn btn-primary btn-sm chat-new" onClick={handleNew}>
            ＋ 新对话
          </button>
          <button type="button" className="chat-side-close" onClick={() => setSideOpen(false)}>
            ×
          </button>
        </div>

        {booting ? (
          <p className="chat-side-empty muted">加载中…</p>
        ) : sessions.length === 0 ? (
          <p className="chat-side-empty muted">还没有聊过，右边随便说点什么吧。</p>
        ) : (
          <ul className="chat-list">
            {sessions.map((session) => (
              <li
                key={session.id}
                className={`chat-list-item ${Number(session.id) === currentId ? 'is-active' : ''}`}
                onClick={() => {
                  setSideOpen(false);
                  navigate(`/chat/${session.id}`);
                }}
              >
                {editing.id === Number(session.id) ? (
                  <input
                    className="chat-list-input"
                    value={editing.title}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setEditing((prev) => ({ ...prev, title: e.target.value }))}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename();
                      if (e.key === 'Escape') setEditing({ id: 0, title: '' });
                    }}
                  />
                ) : (
                  <>
                    <div className="chat-list-main">
                      <span className="chat-list-title">{session.title}</span>
                      <span className="chat-list-time muted">{fromNow(session.last_message_at)}</span>
                    </div>

                    <div className="chat-list-ops">
                      <button type="button" title="改名字" onClick={(e) => startRename(e, session)}>
                        ✎
                      </button>
                      <button type="button" title="删除" onClick={(e) => handleRemove(e, session)}>
                        ✕
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="chat-side-foot">
          <Link to="/" className="muted">
            ← 回去纠结一件事
          </Link>
        </div>
      </aside>

      {sideOpen && <div className="chat-mask" onClick={() => setSideOpen(false)} />}

      {/* ---------------- 右侧：消息流 ---------------- */}
      <section className="chat-main">
        <header className="chat-head">
          <button type="button" className="chat-side-toggle" onClick={() => setSideOpen(true)}>
            ☰
          </button>
          <h2 className="chat-head-title">{current ? current.title : '和想想聊聊'}</h2>
          <span className="muted chat-head-note">
            {current ? `${current.message_count} 条` : '想说什么就说什么'}
          </span>
        </header>

        {!enabled && (
          <div className="chat-banner">AI 暂时不可用：密钥没配好或已被关掉，先看看别处吧。</div>
        )}

        <div className="chat-body" ref={bodyRef}>
          {loadingMsgs && <p className="muted chat-loading">正在翻聊天记录…</p>}

          {!loadingMsgs && messages.length === 0 && (
            <div className="chat-intro">
              <p className="chat-intro-line">
                随便说点什么 —— 今天烦什么、在纠结什么、或者只是想找人念叨两句。
              </p>
              <p className="chat-intro-note muted">
                我不替你做决定，也不打分排名次。就是陪你把它想清楚。
              </p>

              <div className="chat-starters">
                {STARTERS.map((text) => (
                  <button
                    key={text}
                    type="button"
                    className="chat-starter"
                    onClick={() => setInput(text)}
                  >
                    {text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((message) => (
            <AIMessage key={message.id} role={message.role === 'user' ? 'user' : 'ai'}>
              <RichText text={message.content} />
              {message.role !== 'user' && <MessageMeta meta={message.meta} />}
            </AIMessage>
          ))}

          {streaming && (
            <AIMessage role="ai">
              {streamText ? (
                <>
                  <RichText text={streamText} />
                  <span className="chat-caret" />
                </>
              ) : (
                <span className="dots">
                  <i />
                  <i />
                  <i />
                </span>
              )}
            </AIMessage>
          )}

          {error !== '' && (
            <div className="chat-error">
              <span>{error}</span>
              {retryText !== '' && (
                <button type="button" className="chat-retry" onClick={() => submit(retryText)}>
                  重试
                </button>
              )}
            </div>
          )}
        </div>

        <div className="chat-foot">
          <textarea
            className="chat-input"
            ref={inputRef}
            rows={1}
            placeholder="说点什么..."
            value={input}
            disabled={!enabled}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            className="btn btn-primary chat-send"
            disabled={!canSend || !enabled}
            onClick={() => submit(input)}
          >
            {streaming ? '想想中…' : '发送'}
          </button>
        </div>
      </section>
    </div>
  );
}
